import { useEffect, useMemo, useState } from 'react';
import { searchRestaurants } from '../../lib/api.js';
import { color, font, radius } from '../../theme/tokens.js';
import { MESSAGE_GROUP_DECK_SIZE } from './deckSetup.js';

/** The host's restaurant picker inside the expanded iMessage sheet. */
export function GroupDeckSetup({ restaurants, loading, coords, onStart }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const originKey = coords?.join(',') ?? '';
  const trimmedQuery = query.trim();

  useEffect(() => {
    if (trimmedQuery.length < 3) {
      setSearchResults([]);
      setSearching(false);
      setSearchFailed(false);
      return undefined;
    }

    const controller = new AbortController();
    setSearchResults([]);
    setSearching(true);
    setSearchFailed(false);
    const timer = window.setTimeout(() => {
      searchRestaurants(trimmedQuery, coords, controller.signal)
        .then(setSearchResults)
        .catch((error) => {
          if (error.name !== 'AbortError') {
            setSearchResults([]);
            setSearchFailed(true);
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setSearching(false);
        });
    }, 350);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
    // A coordinate value, not an array identity, controls the search bias.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originKey, trimmedQuery]);

  const visible = useMemo(() => {
    if (trimmedQuery.length >= 3) return searchResults;
    const needle = trimmedQuery.toLowerCase();
    if (!needle) return restaurants;
    return restaurants.filter((restaurant) =>
      [restaurant.name, restaurant.cuisine, restaurant.neighborhood]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(needle))
    );
  }, [restaurants, searchResults, trimmedQuery]);

  function toggle(restaurant) {
    setSelected((current) => {
      if (current.some((item) => item.id === restaurant.id)) {
        return current.filter((item) => item.id !== restaurant.id);
      }
      if (current.length >= MESSAGE_GROUP_DECK_SIZE) return current;
      return [...current, restaurant];
    });
  }

  if (loading && trimmedQuery.length < 3) {
    return (
      <div style={styles.centered}>
        <span className="spinner" style={{ width: 22, height: 22 }} />
        <p style={styles.note}>Finding restaurants…</p>
      </div>
    );
  }

  return (
    <section style={styles.root} aria-label="Choose restaurants for the group">
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>Choose restaurants</h1>
          <p style={styles.note}>Pick up to {MESSAGE_GROUP_DECK_SIZE} for everyone to swipe.</p>
        </div>
        <strong style={styles.count}>{selected.length}</strong>
      </header>

      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search any restaurant"
        aria-label="Search restaurants"
        style={styles.search}
      />
      {trimmedQuery.length >= 3 && <p style={styles.attribution}>Powered by Google Maps</p>}
      {selected.length > 0 && (
        <div style={styles.selectedTray} aria-label="Selected restaurants">
          {selected.map((restaurant, index) => (
            <button
              key={restaurant.id}
              type="button"
              onClick={() => toggle(restaurant)}
              aria-label={`Remove ${restaurant.name}`}
              style={styles.selectedPill}
            >
              <span style={styles.selectedIndex}>{index + 1}</span>
              <span style={styles.selectedName}>{restaurant.name}</span>
              <span aria-hidden="true">×</span>
            </button>
          ))}
        </div>
      )}

      <div style={styles.list}>
        {searching && (
          <div style={styles.searching}>
            <span className="spinner" style={{ width: 18, height: 18 }} />
            <span>Searching restaurants…</span>
          </div>
        )}
        {searchFailed && (
          <p style={styles.empty}>Restaurant search is unavailable. Check your connection.</p>
        )}
        {visible.map((restaurant) => {
          const position = selected.findIndex((item) => item.id === restaurant.id);
          const isSelected = position !== -1;
          const disabled = !isSelected && selected.length >= MESSAGE_GROUP_DECK_SIZE;
          return (
            <button
              key={restaurant.id}
              type="button"
              onClick={() => toggle(restaurant)}
              disabled={disabled}
              aria-pressed={isSelected}
              style={{
                ...styles.row,
                ...(isSelected ? styles.rowSelected : null),
                ...(disabled ? styles.rowDisabled : null),
              }}
            >
              <span style={styles.restaurantText}>
                <strong style={styles.name}>{restaurant.name}</strong>
                <span style={styles.meta}>
                  {[restaurant.cuisine, restaurant.neighborhood, restaurant.price]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <span style={{ ...styles.check, ...(isSelected ? styles.checkSelected : null) }}>
                {isSelected ? position + 1 : ''}
              </span>
            </button>
          );
        })}
        {!searching && !searchFailed && visible.length === 0 && (
          <p style={styles.empty}>
            {trimmedQuery.length < 3
              ? 'Type at least 3 characters to search any restaurant.'
              : 'No restaurants match that search.'}
          </p>
        )}
      </div>

      <footer style={styles.footer}>
        <button
          type="button"
          disabled={selected.length === 0}
          onClick={() => onStart(selected)}
          style={{ ...styles.start, ...(selected.length === 0 ? styles.startDisabled : null) }}
        >
          Start group with {selected.length || 0}
        </button>
      </footer>
    </section>
  );
}

const styles = {
  root: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    background: color.surface,
  },
  centered: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '8px 10px 10px',
  },
  title: { margin: 0, fontSize: 18, fontWeight: font.weight.heavy, color: color.ink },
  note: { margin: '3px 0 0', fontSize: 12, color: color.inkFaint },
  count: {
    display: 'grid',
    placeItems: 'center',
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: color.primary,
    color: color.inkInverse,
    fontSize: 13,
  },
  search: {
    margin: '0 10px 8px',
    padding: '10px 12px',
    border: `1px solid ${color.border}`,
    borderRadius: radius.md,
    background: color.surface,
    color: color.ink,
    fontSize: 13,
  },
  attribution: {
    margin: '-3px 12px 7px',
    textAlign: 'right',
    fontSize: 9,
    color: color.inkFaint,
  },
  selectedTray: {
    display: 'flex',
    gap: 6,
    overflowX: 'auto',
    padding: '0 10px 8px',
  },
  selectedPill: {
    flex: '0 0 auto',
    maxWidth: 190,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 8px',
    border: `1px solid ${color.primaryBorder}`,
    borderRadius: radius.pill,
    background: color.primarySoft,
    color: color.primaryDark,
    fontSize: 11,
    cursor: 'pointer',
  },
  selectedIndex: {
    display: 'grid',
    placeItems: 'center',
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: color.primary,
    color: color.inkInverse,
    fontSize: 9,
    fontWeight: font.weight.heavy,
  },
  selectedName: {
    maxWidth: 130,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  list: { flex: 1, minHeight: 0, overflowY: 'auto', borderTop: `1px solid ${color.border}` },
  searching: {
    minHeight: 54,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    fontSize: 12,
    color: color.inkMuted,
  },
  row: {
    width: '100%',
    minHeight: 54,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: '9px 12px',
    border: 'none',
    borderBottom: `1px solid ${color.border}`,
    background: color.surface,
    color: color.ink,
    textAlign: 'left',
    cursor: 'pointer',
  },
  rowSelected: { background: color.primarySoft },
  rowDisabled: { opacity: 0.45, cursor: 'default' },
  restaurantText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  name: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13.5 },
  meta: {
    marginTop: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 11,
    color: color.inkMuted,
  },
  check: {
    flexShrink: 0,
    width: 24,
    height: 24,
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
    border: `1px solid ${color.borderStrong}`,
    color: color.inkInverse,
    fontSize: 11,
    fontWeight: font.weight.bold,
  },
  checkSelected: { borderColor: color.primary, background: color.primary },
  empty: { margin: '36px 20px', textAlign: 'center', fontSize: 13, color: color.inkFaint },
  footer: { padding: '10px', borderTop: `1px solid ${color.border}` },
  start: {
    width: '100%',
    padding: '11px 16px',
    border: 'none',
    borderRadius: radius.md,
    background: color.primary,
    color: color.inkInverse,
    fontSize: 14,
    fontWeight: font.weight.bold,
    cursor: 'pointer',
  },
  startDisabled: { opacity: 0.45, cursor: 'default' },
};
